import { useEffect, useMemo, useState } from "react";
import { Brand } from "../components/Brand";
import { Banner } from "../components/ui";
import { ApiError, type Api } from "../lib/api";
import { parseTokenizedQuery } from "../lib/queryParams";
import type { AccessRequest } from "../lib/types";

type State =
  | { status: "loading" }
  | { status: "ready"; request: AccessRequest }
  | { status: "decided"; kind: "approved" | "denied"; actionLink?: string }
  | { status: "error"; message: string };

/**
 * Public, token-gated approval page for self-service access requests (scope change).
 * Reached from the tokenized link emailed to support. No portal session required —
 * the one-time token in the URL authorizes the decision, verified server-side.
 */
export function Approve({ api }: { api: Api }) {
  const params = parseTokenizedQuery(window.location.search);
  const id = params.get("id") ?? "";
  const token = params.get("token") ?? "";
  const [state, setState] = useState<State>({ status: "loading" });
  const [busy, setBusy] = useState(false);

  const canLoad = useMemo(() => Boolean(id && token), [id, token]);

  useEffect(() => {
    if (!canLoad) {
      setState({ status: "error", message: "This approval link is missing its id or token." });
      return;
    }
    let cancelled = false;
    api
      .getAccessRequest(id, token)
      .then((r) => {
        if (!cancelled) setState({ status: "ready", request: r.request });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: "error",
          message:
            err instanceof ApiError
              ? err.message
              : "Could not load this access request.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [api, id, token, canLoad]);

  async function decide(kind: "approved" | "denied") {
    setBusy(true);
    try {
      if (kind === "approved") {
        const res = await api.approveAccessRequest(id, { token });
        setState({ status: "decided", kind, actionLink: res.actionLink });
      } else {
        await api.denyAccessRequest(id, token);
        setState({ status: "decided", kind });
      }
    } catch (err) {
      setState({
        status: "error",
        message: err instanceof ApiError ? err.message : "The decision could not be recorded.",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <Brand />
        </div>
        <div className="card p-8">
          <div className="mb-6">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-accent">
              Access review
            </div>
            <h1 className="mt-1 text-xl font-semibold text-ink">Approve portal access</h1>
            <p className="mt-1 text-sm text-ink-muted">
              This link carries a one-time approval token. Decisions are authorized and audited
              server-side.
            </p>
          </div>

          {state.status === "loading" && (
            <p className="text-sm text-ink-muted">Loading request…</p>
          )}

          {state.status === "error" && <Banner tone="error">{state.message}</Banner>}

          {state.status === "ready" && (
            <>
              <dl className="space-y-2 text-sm">
                <Row label="Email" value={state.request.email} mono />
                {state.request.name && <Row label="Name" value={state.request.name} />}
                {state.request.company && <Row label="Organization" value={state.request.company} />}
                {state.request.message && <Row label="Reason" value={state.request.message} />}
                <Row
                  label="Requested"
                  value={new Date(state.request.createdAt).toLocaleString()}
                />
                <Row label="Status" value={state.request.status} mono />
              </dl>

              {state.request.status !== "pending" ? (
                <div className="mt-4">
                  <Banner tone="info">
                    This request is already {state.request.status}.
                  </Banner>
                </div>
              ) : (
                <div className="mt-6 space-y-4">
                  <Banner tone="warning">
                    Approving from this link provisions the user into a{" "}
                    <strong>new, isolated account</strong>. If they belong to a customer that
                    already has a portal account, approve them from the admin console instead
                    (Directory → Access requests) so you can associate them to the existing
                    account and its logs.{" "}
                    <a
                      href="/app/directory"
                      className="text-accent-strong hover:underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open the console
                    </a>
                    .
                  </Banner>
                  <div className="flex gap-3">
                  <button
                    className="btn-primary flex-1"
                    disabled={busy}
                    onClick={() => void decide("approved")}
                  >
                    {busy ? "Working…" : "Approve new account"}
                  </button>
                  <button
                    className="btn-ghost flex-1"
                    disabled={busy}
                    onClick={() => void decide("denied")}
                  >
                    Deny
                  </button>
                  </div>
                </div>
              )}
            </>
          )}

          {state.status === "decided" && (
            <div className="space-y-4">
              <Banner tone={state.kind === "approved" ? "success" : "info"}>
                {state.kind === "approved"
                  ? "Approved. The user has been provisioned and a set-password email is on its way."
                  : "Request denied. No account was created."}
              </Banner>
              {state.kind === "approved" && state.actionLink && (
                <p className="break-all text-xs text-ink-dim">
                  Set-password link (in case the email doesn't arrive):{" "}
                  <a
                    href={state.actionLink}
                    className="text-accent-strong hover:underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {state.actionLink}
                  </a>
                </p>
              )}
            </div>
          )}
        </div>
        <p className="mt-6 text-center text-xs text-ink-dim">
          PumaMesh portal · authorized reviewers only
        </p>
      </div>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-ink-dim">{label}</dt>
      <dd className={`text-right text-ink ${mono ? "font-mono text-xs" : ""}`}>{value}</dd>
    </div>
  );
}
